import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RwAvatar } from '@components/atoms';
import { RwEmpty } from '@components/RwEmpty';
import { joomlaOptions } from '@services/joomla';
import { t } from '@services/i18n';
import { visibleUserNav, type MemberScreenId } from './navigation';
import { MEMBER_SCREENS } from './screens';
import { RoutedAnchor } from './RoutedAnchor';
import { ROUTE_STATE_KEY, navTarget } from './routes';
import { useResolvedRoute } from './useResolvedRoute';

function initialsOf(name?: string | null): string {
  if (!name) return 'ME';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'ME';
  const a = parts[0][0] ?? '';
  const b = parts.length > 1 ? parts[parts.length - 1][0] : parts[0][1] ?? '';
  return (a + b).toUpperCase();
}

/**
 * Final client-side guard on the custom header logo URL. The server already
 * resolves and scheme-checks it; here we accept only an http(s), protocol-
 * relative, or root-relative URL, and reject anything else (e.g. javascript:).
 */
function safeLogoUrl(raw?: string): string {
  const url = (raw ?? '').trim();
  if (!url) return '';
  return /^https?:\/\//i.test(url) || url.startsWith('//') || url.startsWith('/') ? url : '';
}

/**
 * Member shell — horizontal top-tab chrome + the active member screen. The
 * active tab is the resolved SEF route (BrowserRouter), so every tab is a real,
 * deep-linkable URL. Tabs are RoutedAnchors (real href + client navigate);
 * `setScreen`, still passed to screens for programmatic nav, does the same.
 */
export function UserShell() {
  const navigate = useNavigate();
  const screen = useResolvedRoute();
  const Screen = MEMBER_SCREENS[screen];
  const user = joomlaOptions().user;
  const nav = visibleUserNav(joomlaOptions().nav);
  const loginUrl = joomlaOptions().loginUrl;

  // Member-header branding (Appearance settings). A custom image falls back to
  // the built-in mark when its URL is missing or rejected; 'hidden' shows none.
  const header = joomlaOptions().header;
  const logoUrl = safeLogoUrl(header.logoUrl);
  const markText = (header.markText ?? '').trim() || 'R';
  const showLogoImg = header.logo === 'custom' && logoUrl !== '';
  const showLogoMark = header.logo === 'default' || (header.logo === 'custom' && logoUrl === '');
  const showBrand = showLogoImg || showLogoMark || header.showTitle;

  const setScreen = useCallback(
    (id: MemberScreenId) => {
      const { target, route } = navTarget(id);
      navigate(target, { state: { [ROUTE_STATE_KEY]: route } });
    },
    [navigate],
  );

  return (
    <div className="rw-user-shell">
      <div className="rw-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {showLogoImg && (
            <img className="rw-brand-logo" src={logoUrl} alt="" style={{ height: 26, width: 'auto', display: 'block' }} />
          )}
          {showLogoMark && (
            <div className="rw-brand-mark" style={{ width: 26, height: 26, fontSize: 16 }}>
              {markText}
            </div>
          )}
          {header.showTitle && (
            <div className="rw-brand-name" style={{ fontSize: 16 }}>
              {t('COM_REWARDIFY', 'Rewardify')}
            </div>
          )}
          {showBrand && <div style={{ width: 1, height: 20, background: 'var(--rw-line)', margin: '0 6px' }} />}
          <nav className="rw-user-tabs">
            {nav.map((n) => {
              const { href, route } = navTarget(n.id);
              return (
                <RoutedAnchor
                  key={n.id}
                  className={`rw-user-tab ${screen === n.id ? 'on' : ''}`}
                  href={href}
                  to={route}
                  aria-current={screen === n.id ? 'page' : undefined}
                >
                  {t(n.labelKey, n.label)}
                </RoutedAnchor>
              );
            })}
          </nav>
        </div>
        <div className="rw-topbar-r">
          {user.guest && loginUrl ? (
            <a
              href={loginUrl}
              className="rw-topbar-login"
              title={t('COM_REWARDIFY_LOGIN', 'Log in')}
              aria-label={t('COM_REWARDIFY_LOGIN', 'Log in')}
              style={{ display: 'inline-flex', textDecoration: 'none' }}
            >
              <RwAvatar initials={initialsOf(user.name)} size={30} />
            </a>
          ) : (
            <RwAvatar initials={initialsOf(user.name)} size={30} />
          )}
        </div>
      </div>
      <main className="rw-main">
        {user.guest ? (
          <div className="rw-page rw-user">
            <RwEmpty
              title={t('COM_REWARDIFY_LOGIN_REQUIRED_TITLE', 'Sign in to view your rewards')}
              hint={t(
                'COM_REWARDIFY_LOGIN_REQUIRED_HINT',
                'Your balances, activity, badges, campaigns, and redemptions are available after you sign in.',
              )}
            />
            {loginUrl ? (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <a className="rw-btn rw-btn-primary" href={loginUrl}>
                  {t('COM_REWARDIFY_LOGIN', 'Log in')}
                </a>
              </div>
            ) : null}
          </div>
        ) : (
          <Screen setScreen={setScreen} />
        )}
      </main>
    </div>
  );
}
